/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be 'public', 'private', or 'metadata'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Sharetribe CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
// CUSTOM: listing-config — entire listingFields array is domain-specific (antique maps)
export const listingFields = [
  {
    key: 'map_title',
    scope: 'public',
    // includeForListingTypes: ['product-selling'],
    schemaType: 'text',
    showConfig: {
      label: 'Map title',
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Map title',
      group: 'primary',
    },
    saveConfig: {
      label: 'Map title',
      isRequired: true,
      placeholderMessage: 'Title or titles from the cartouches; use ... to truncate if needed',
      requiredMessage: 'A map title is required; use [Untitled] if unknown'
    },
  },
  {
    key: 'publisher',
    scope: 'public',
    schemaType: 'text',
    filterConfig: {
      indexForSearch: true,
      label: 'Publisher',
      group: 'primary',
    },
    showConfig: {
      label: 'Publisher',
      isDetail: true,
    },
    saveConfig: {
      label: 'Publisher',
      placeholderMessage: '',
      isRequired: true,
      requiredMessage: 'List all known names associated with publication; use [Anonymous] if unknown',
    },
  },
  {
    key: 'publication_date',
    scope: 'public',
    schemaType: 'long',
    filterConfig: {
      indexForSearch: false,
      label: 'Publication date',
      group: 'primary',
    },
    showConfig: {
      label: 'Publication date',
      isDetail: true,
    },
    saveConfig: {
      label: 'Publication date',
      placeholderMessage: '',
      isRequired: true,
      requiredMessage: 'A publication date is required; estimate it if unknown',
    },
  },
  {
    key: 'map_condition',
    scope: 'public',
    // includeForListingTypes: ['product-selling'],
    schemaType: 'text',
    showConfig: {
      label: 'Map condition',
    },
    filterConfig: {
      indexForSearch: false,
      label: 'Map condition',
      group: 'primary',
    },
    saveConfig: {
      label: 'Map condition',
      isRequired: true,
      placeholderMessage: "Note all flaws. Do not use grading systems such as 'Fine' or 'B–'.",
      requiredMessage: "You need to describe the condition.",
    },
  },
  {
    key: 'category_of_artifact',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'atlas', label: 'Atlas' },
      { option: 'geography', label: 'Geography' },
      { option: 'globe', label: 'Globe' },
      { option: 'reference_book', label: 'Reference book' },
      { option: 'collection', label: 'Collection' },
      { option: 'world_map', label: 'World map' },
      { option: 'hemispheric_map', label: 'Hemispheric map' },
      { option: 'continental_map', label: 'Continental map' },
      { option: 'oceanic_map', label: 'Oceanic map' },
      { option: 'regional_map', label: 'Regional map' },
      { option: 'national_map', label: 'National map' },
      { option: 'provincial_map', label: 'Provincial map' },
      { option: 'city_map', label: 'City map' },
      { option: 'nautical_chart', label: 'Nautical chart' },
      { option: 'celestial_map', label: 'Celestial map' },
      { option: 'title_page', label: 'Title page' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      showFilter: true,
      label: 'Category',
      group: 'primary',
    },
    showConfig: {
      label: 'Category',
      isDetail: true,
    },
    saveConfig: {
      label: 'Category',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select a category.',
    },
  },
  {
    key: 'region',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'world', label: 'World' },
      { option: 'eastern_hemisphere', label: 'Eastern hemisphere' },
      { option: 'western_hemisphere', label: 'Western hemisphere' },
      { option: 'northern_hemisphere', label: 'Northern hemisphere' },
      { option: 'southern_hemisphere', label: 'Southern hemisphere' },
      { option: 'north_pole', label: 'North pole' },
      { option: 'south_pole', label: 'South pole' },
      { option: 'north_america', label: 'North America' },
      { option: 'south_america', label: 'South America' },
      { option: 'africa', label: 'Africa' },
      { option: 'antarctica', label: 'Antarctica' },
      { option: 'asia', label: 'Asia' },
      { option: 'middle_east', label: 'Middle East' },
      { option: 'east_asia', label: 'East Asia' },
      { option: 'australia', label: 'Australia' },
      { option: 'europe', label: 'Europe' },
      { option: 'pacific_ocean', label: 'Pacific Ocean' },
      { option: 'atlantic_ocean', label: 'Atlantic Ocean' },
      { option: 'indian_ocean', label: 'Indian Ocean' },
      { option: 'mediterranean', label: 'Mediterranean' },
      { option: 'caribbean', label: 'Caribbean' },
      { option: 'not_earth', label: '(not the earth)' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      showFilter: true,
      label: 'Mapped region',
      group: 'primary',
    },
    showConfig: {
      label: 'Mapped region',
      isDetail: true,
    },
    saveConfig: {
      label: 'Mapped region',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select a region.',
    },
  },
  {
    key: 'paper_width',
    scope: 'public',
    schemaType: 'long',
    filterConfig: {
      indexForSearch: false,
      label: 'Paper width (mm)',
      group: 'primary',
    },
    showConfig: {
      // key: 'paper_width',
      label: 'Paper width (mm)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Paper width (mm)',
      placeholderMessage: '',
      isRequired: true,
      requiredMessage: 'You need to specify a paper width in millimeters',
    },
  },
  {
    key: 'paper_height',
    scope: 'public',
    schemaType: 'long',
    filterConfig: {
      indexForSearch: false,
      label: 'Paper height (mm)',
      group: 'primary',
    },
    showConfig: {
      // key: 'paper_width',
      label: 'Paper height (mm)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Paper height (mm)',
      placeholderMessage: '',
      isRequired: true,
      requiredMessage: 'You need to specify a paper height in millimeters',
    },
  },
  {
    key: 'printed_width',
    scope: 'public',
    schemaType: 'long',
    filterConfig: {
      indexForSearch: false,
      label: 'Printed width (mm)',
      group: 'primary',
    },
    showConfig: {
      // key: 'paper_width',
      label: 'Printed width (mm)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Printed width (mm)',
      placeholderMessage: '',
      isRequired: true,
      requiredMessage: 'You need to set a printed width; set 0 for multiple artifacts',
    },
  },
  {
    key: 'printed_height',
    scope: 'public',
    schemaType: 'long',
    filterConfig: {
      indexForSearch: false,
      label: 'Printed height (mm)',
      group: 'primary',
    },
    showConfig: {
      // key: 'paper_width',
      label: 'Printed height (mm)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Printed height (mm)',
      placeholderMessage: '',
      isRequired: true,
      requiredMessage: 'You need to set a printed height; set 0 for multiple artifacts',
    },
  },
  {
    key: 'stock_id',
    scope: 'public',
    // includeForListingTypes: ['product-selling'],
    schemaType: 'text',
    showConfig: {
      label: 'Stock ID',
      hideFromPublic: true,
    },
    filterConfig: {
      indexForSearch: false,
      label: 'Stock ID',
      group: 'primary',
    },
    saveConfig: {
      label: 'Stock ID',
      placeholderMessage: 'Your own stock ID, if you use one. CAUTION: Not secure.',
    },
  },

  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem', 'multipleItems', 'infiniteOneItem', and 'infiniteMultipleItems'.
 *                        Default: 'multipleItems'.
 * - availabilityType     This is relevant only to listings using default-booking process.
 *                        If set to 'oneSeat', seat management is not showed and the listing is
 *                        considered per person (seat = 1).
 *                        Possible values: 'oneSeat' and 'multipleSeats'.
 *                        Default: 'oneSeat'.
 * - priceVariations      This is relevant only to listings using default-booking process.
 *   - enabled:             If set to true, price variations are enabled.
 *                          Default: false.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 * - transactionFields    You can define an array of custom transaction fields for each listing type. Each transaction field
 *                        should have the following attributes:
 *                        - key (string)
 *                        - label (string)
 *                        - showTo (string, options: 'customer', 'provider'). Option 'provider' is only used for negotiation process.
 *                        - schemaType (string, options: 'enum', 'multi-enum', 'text', 'long', 'boolean', 'youtubeVideoUrl')
 *                        - saveConfig (object, optional,  { required: true })
 *                        - schema specific attributes:
 *                          - numberConfig (object, for schemaType: 'long'): { minimum: number, maximum: number }
 *                          - enumOptions (array, for schemaType: 'enum', 'multi-enum'): [{ label: string, option: string }]
 */

// CUSTOM: listing-config — single listing type for antique maps
export const listingTypes = [
  {
    listingType: 'product-selling',
    label: 'Maps',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-4',
      unitType: 'item',
    },
    stockType: 'oneItem',
  },
  // // Here are some examples of listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'day',
  //   },
  //   availabilityType: 'oneSeat',
  //   defaultListingFields: {
  //     location: true,
  //     payoutDetails: true,
  //   },
  //   transactionFields: [
  //     {
  //       showTo: 'customer',
  //       label: 'Extra requests for the hosts',
  //       key: 'requests',
  //       schemaType: 'text',
  //     },
  //     {
  //       showTo: 'customer',
  //       label: 'Are you traveling with minors?',
  //       key: 'minors',
  //       schemaType: 'boolean',
  //     },
  //     {
  //       showTo: 'customer',
  //       numberConfig: {
  //         minimum: 1,
  //         maximum: 10,
  //       },
  //       label: 'How many people are staying at the venue',
  //       key: 'peopleStaying',
  //       schemaType: 'long',
  //       saveConfig: {
  //         required: true,
  //       },
  //     },
  //     {
  //       showTo: 'customer',
  //       enumOptions: [
  //         {
  //           label: 'Morning cleanup (10am-12am)',
  //           option: 'morning',
  //         },
  //         {
  //           label: 'Afternoon cleanup (2pm-4pm)',
  //           option: 'afternoon',
  //         },
  //       ],
  //       label: 'Schedule preference',
  //       key: 'schedulePreference',
  //       schemaType: 'enum',
  //     },
  //     {
  //       showTo: 'customer',
  //       enumOptions: [
  //         {
  //           label: 'Vegetarian',
  //           option: 'vegetarian',
  //         },
  //         {
  //           label: 'Vegan',
  //           option: 'vegan',
  //         },
  //         {
  //           label: 'Gluten free',
  //           option: 'glutenFree',
  //         },
  //         {
  //           label: 'No caffeine',
  //           option: 'decaf',
  //         },
  //         {
  //           label: 'Nut free',
  //           option: 'nutFree',
  //         },
  //         {
  //           label: 'Dairy free',
  //           option: 'dairyFree',
  //         },
  //       ],
  //       label: 'Dietary preferences',
  //       key: 'dietaryPreferences',
  //       schemaType: 'multi-enum',
  //     },
  //   ],
  // },
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
